import type {
  AnnualLeaveResponse,
  UnpaidLeaveResponse,
  MedicalLeaveResponse,
  Worker,
  CreatedBy,
} from "@/features/hr/leave-applications/types";
import type { SelfServiceRequest } from "../types";
import type { WorkerProfile } from "../../shared/types";

function createWorkerFromProfile(profile: WorkerProfile): Worker {
  return {
    id: profile.id,
    iin: profile.iin,
    full_name: profile.full_name,
    date_of_birth: "",
    gender: "other",
    status: profile.status,
    hired_date: profile.start_date || "",
    created_at: "",
    job_position: profile.job_position_ru || profile.job_position_kk || null,
  };
}

function createCreatedBy(profile: WorkerProfile): CreatedBy {
  return {
    id: profile.id,
    full_name: profile.full_name,
  };
}

export function transformSelfRequestToAnnualLeave(
  selfRequest: SelfServiceRequest,
  profile: WorkerProfile,
): AnnualLeaveResponse | null {
  if (selfRequest.type !== "annual_leave" || !selfRequest.date_range) {
    return null;
  }

  return {
    id: selfRequest.id,
    status: selfRequest.status as AnnualLeaveResponse["status"],
    organization: 0,
    created_by: createCreatedBy(profile),
    worker: createWorkerFromProfile(profile),
    start_date: selfRequest.date_range.start_date,
    end_date: selfRequest.date_range.end_date,
    days_count: selfRequest.date_range.days_count || 0,
    reason: (selfRequest.details?.reason as string) || null,
    is_completed: selfRequest.details?.is_completed || false,
    signing_status: selfRequest.details?.signing_status ?? null,
    signing_url: (selfRequest.details?.signing_url as string | null) || null,
    signed_pdf_url: (selfRequest.details?.signed_pdf_url as string | null) || null,
    original_file_url: (selfRequest.details?.original_file_url as string | null) || null,
    application_signed_pdf_key: null,
    application_signed_pdf_url: null,
    application_uploaded_at: null,
    application_review_status: null,
    application_review_note: null,
    application_reviewed_at: null,
    application_reviewed_by: null,
    application_reviewed_by_name: null,
    application_document: { original_url: null, signed_url: null },
    order_document: { original_url: null, signed_url: null },
    order: null,
    created_at: selfRequest.created_at,
    updated_at: selfRequest.created_at,
  };
}

export function transformSelfRequestToUnpaidLeave(
  selfRequest: SelfServiceRequest,
  profile: WorkerProfile,
): UnpaidLeaveResponse | null {
  if (selfRequest.type !== "unpaid_leave" || !selfRequest.date_range) {
    return null;
  }

  return {
    id: selfRequest.id,
    status: selfRequest.status as UnpaidLeaveResponse["status"],
    organization: 0,
    created_by: createCreatedBy(profile),
    worker: createWorkerFromProfile(profile),
    start_date: selfRequest.date_range.start_date,
    end_date: selfRequest.date_range.end_date,
    days_count: selfRequest.date_range.days_count || 0,
    reason: (selfRequest.details?.reason as string) || "",
    approval_resolution:
      (selfRequest.details?.approval_resolution as "approved" | "recommend" | "no_objection" | "" | null) || null,
    approval_resolution_display: null,
    is_completed: selfRequest.details?.is_completed || false,
    signing_status: selfRequest.details?.signing_status ?? null,
    signing_url: (selfRequest.details?.signing_url as string | null) || null,
    signed_pdf_url: (selfRequest.details?.signed_pdf_url as string | null) || null,
    original_file_url: (selfRequest.details?.original_file_url as string | null) || null,
    application_signed_pdf_key: null,
    application_signed_pdf_url: null,
    application_uploaded_at: null,
    application_review_status: null,
    application_review_note: null,
    application_reviewed_at: null,
    application_reviewed_by: null,
    application_reviewed_by_name: null,
    application_document: { original_url: null, signed_url: null },
    order_document: { original_url: null, signed_url: null },
    order: null,
    created_at: selfRequest.created_at,
    updated_at: selfRequest.created_at,
  };
}

export function transformSelfRequestToMedicalLeave(
  selfRequest: SelfServiceRequest,
  profile: WorkerProfile,
): MedicalLeaveResponse | null {
  if (selfRequest.type !== "medical_leave" || !selfRequest.date_range) {
    return null;
  }

  return {
    id: selfRequest.id,
    status: selfRequest.status as MedicalLeaveResponse["status"],
    organization: 0,
    created_by: createCreatedBy(profile),
    worker: createWorkerFromProfile(profile),
    start_date: selfRequest.date_range.start_date,
    end_date: selfRequest.date_range.end_date,
    days_count: selfRequest.date_range.days_count || 0,
    diagnosis: (selfRequest.details?.diagnosis as string) || null,
    certificate_pdf_key: null,
    certificate_pdf_url: null,
    certificate_uploaded_at: null,
    certificate_required:
      selfRequest.details?.certificate_required !== undefined
        ? Boolean(selfRequest.details.certificate_required)
        : true,
    is_completed: selfRequest.details?.is_completed || false,
    signing_status: selfRequest.details?.signing_status ?? null,
    signing_url: (selfRequest.details?.signing_url as string | null) || null,
    signed_pdf_url: (selfRequest.details?.signed_pdf_url as string | null) || null,
    original_file_url: (selfRequest.details?.original_file_url as string | null) || null,
    application_signed_pdf_key: null,
    application_signed_pdf_url: null,
    application_uploaded_at: null,
    application_review_status: null,
    application_review_note: null,
    application_reviewed_at: null,
    application_reviewed_by: null,
    application_reviewed_by_name: null,
    application_document: { original_url: null, signed_url: null },
    order_document: { original_url: null, signed_url: null },
    order: null,
    created_at: selfRequest.created_at,
    updated_at: selfRequest.created_at,
  };
}

export function transformSelfRequestsToAnnualLeaves(
  requests: SelfServiceRequest[],
  profile: WorkerProfile,
): AnnualLeaveResponse[] {
  return requests
    .map((req) => transformSelfRequestToAnnualLeave(req, profile))
    .filter((leave): leave is AnnualLeaveResponse => leave !== null);
}

export function transformSelfRequestsToUnpaidLeaves(
  requests: SelfServiceRequest[],
  profile: WorkerProfile,
): UnpaidLeaveResponse[] {
  return requests
    .map((req) => transformSelfRequestToUnpaidLeave(req, profile))
    .filter((leave): leave is UnpaidLeaveResponse => leave !== null);
}

export function transformSelfRequestsToMedicalLeaves(
  requests: SelfServiceRequest[],
  profile: WorkerProfile,
): MedicalLeaveResponse[] {
  return requests
    .map((req) => transformSelfRequestToMedicalLeave(req, profile))
    .filter((leave): leave is MedicalLeaveResponse => leave !== null);
}
